import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

// Drives the social feed post modal's open/closed state purely off the `post`/`comment` query
// params on whatever page is currently showing, instead of navigating to a dedicated route.
// That means opening a post never unmounts the page it was opened from (e.g. a dashboard
// content block) - only the search params change, so the page underneath just re-renders.
export function usePostModalParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const postId = searchParams.get('post') ?? undefined;
  const commentKey = searchParams.get('comment') ?? undefined;

  const openPost = useCallback(
    (postId: string, commentKey?: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('post', postId);
        if (commentKey) {
          next.set('comment', commentKey);
        } else {
          next.delete('comment');
        }
        return next;
      }); // Pushes a new history entry, so the browser back button closes the modal too.
    },
    [setSearchParams]
  );

  const closePost = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('post');
        next.delete('comment');
        return next;
      },
      // Replace rather than push, so closing works even on a cold deep link with no history
      // entry to go back to.
      { replace: true }
    );
  }, [setSearchParams]);

  return { postId, commentKey, openPost, closePost };
}
